import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper
} from '@mui/material';
import {
  TableChart as TableIcon,
  Quiz as QuizIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Header from '../Header';
import CsvQuizQuestionImport from './CsvQuizQuestionImport';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)',
    borderColor: '#4CAF50',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#4CAF50',
  color: '#fff',
  padding: '12px 24px',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: '#45a049',
  },
}));

const QuizImportMaster = () => {
  const [currentView, setCurrentView] = useState('main'); // 'main' or 'csv-import'

  const handleViewChange = (view) => {
    setCurrentView(view); 
  };

  const importOptions = [
    {
      id: 'csv-import',
      title: 'CSV Question Import',
      description: 'Import quiz questions from CSV files with pre-configured settings',
      icon: <TableIcon sx={{ fontSize: 48, color: '#4CAF50' }} />,
      features: [
        'Pre-select question settings',
        'CSV file upload',
        'Bulk question import',
        'Data validation'
      ]
    },
    // {
    //   id: 'excel-import',
    //   title: 'Excel Question Import',
    //   description: 'Import quiz questions from Excel files (existing functionality)',
    //   icon: <FileUploadIcon sx={{ fontSize: 48, color: '#4CAF50' }} />,
    //   features: [
    //     'Excel file support',
    //     'Song-based import',
    //     'Multi-step workflow',
    //     'Question preview'
    //   ],
    // //   disabled: true,
    //   note: 'Available in Quiz Management section'
    // }
  ];

  if (currentView === 'csv-import') {
    return <CsvQuizQuestionImport onBack={() => handleViewChange('main')} />;
  }

  return (
    <>
      <Header />
      <Box sx={{
        maxWidth: 1200,
        margin: '0 auto',
        px: 3,
        py: 4,
        minHeight: '100vh',
        backgroundColor: '#121212'
      }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: '#fff',
              fontWeight: 600,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <QuizIcon sx={{ fontSize: 40, color: '#4CAF50' }} />
            Quiz Import Center
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#ccc',
              fontWeight: 400,
              mb: 3
            }}
          >
            Choose your preferred method to import quiz questions
          </Typography>
        </Box>

        {/* Import Options Grid */}
        <Grid container spacing={3}>
          {importOptions.map((option) => (
            <Grid item xs={12} md={12} key={option.id}>
              <StyledCard
                onClick={() => !option.disabled && handleViewChange(option.id)}
                sx={{
                  opacity: option.disabled ? 0.6 : 1,
                  cursor: option.disabled ? 'not-allowed' : 'pointer',
                  '&:hover': option.disabled ? {} : undefined
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    {option.icon}
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      color: '#fff',
                      fontWeight: 600,
                      mb: 2,
                      textAlign: 'center'
                    }}
                  >
                    {option.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#ccc',
                      mb: 3,
                      textAlign: 'center',
                      lineHeight: 1.6
                    }}
                  >
                    {option.description}
                  </Typography>

                  {/* Features List */}
                  <Box sx={{ mb: 3, justifyContent: 'center', display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                    {option.features.map((feature, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          color: '#4CAF50',
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        • {feature}
                      </Typography>
                    ))}
                  </Box>

                  {/* Action Button or Note */}
                  <Box sx={{ textAlign: 'center' }}>
                    {option.disabled ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#ff9800',
                          fontStyle: 'italic'
                        }}
                      >
                        {option.note}
                      </Typography>
                    ) : (
                      <StyledButton
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewChange(option.id);
                        }}
                      >
                        Get Started
                      </StyledButton>
                    )}
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>

        {/* Additional Information */}
        <Paper
          sx={{
            mt: 4,
            p: 3,
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
            borderRadius: '8px'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: '#4CAF50',
              fontWeight: 600,
              mb: 2
            }}
          >
            Import Guidelines
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#ccc',
              lineHeight: 1.6
            }}
          >
            • Ensure your data follows the required format before importing<br />
            • CSV imports allow you to pre-configure settings that apply to all questions<br />
            • All imported questions will be validated before being added to the database<br />
            • You can preview and edit questions before final import
          </Typography>
        </Paper>
      </Box>
    </>
  );
};

export default QuizImportMaster;